import { IFilledFormV2, IGizmoData, IRootGizmoData } from '../../types';
import { gnov } from '../../utils';
import moment from 'moment-timezone';

interface IParams {
  data: IFilledFormV2;
}

/**
 * Transform v2 root gizmo data to v3
 *
 * @param {IFilledFormV2} data v2 filled form
 *
 * @returns {IRootGizmoData} v3 root gizmo data
 */
function rootGizmoDataV2ToV3({ data }: IParams): IRootGizmoData {
  const rootGizmoData: IRootGizmoData = {
    fid: data.filledFormId,
    rootGizmoFid: data.formId,
    rootGizmoVersion: data.formVersion,
    formId: data.formId,
    formVersion: data.formVersion,
    version: data.version,
    status: data.status,
    epoch_start: data.epoch_start,
    epoch_end: data.epoch_end,
    source: data.source,
    d: {},
  };

  if (data.belongsTo !== undefined) {
    rootGizmoData.belongsTo = data.belongsTo;
  }
  if (data.action !== undefined) {
    rootGizmoData.action = data.action;
  }
  if (data.createdAt) {
    const date = moment.utc(data.createdAt, 'YYYY-MM-DD HH:mm:ss.SSSSSS');
    // rootGizmoData.createdAt = data.createdAt;
    rootGizmoData.createdAt = moment(date).local().toDate();
  }
  if (data.username) {
    rootGizmoData.username = data.username;
  }

  if (rootGizmoData.d && data.answers) {
    for (const fid in data.answers) {
      if (Object.keys(data.answers[fid]).length === 1) {
        const value = gnov(data.answers, `${fid}.0.value`);
        const f_id = gnov(data.answers, `${fid}.0.f_id`);
        const attachmentId = gnov(data.answers, `${fid}.0.attachmentId`);
        const timestamp = gnov(data.answers, `${fid}.0.timestamp`);
        const ocrResult = gnov(data.answers, `${fid}.0.ocrResult`);
        const filledFormId = gnov(data.answers, `${fid}.0.filledFormId`);
        const formId = gnov(data.answers, `${fid}.0.formId`);

        if (value !== undefined) {
          const gizmoData: IGizmoData = { v: value };

          if (
            f_id !== undefined &&
            filledFormId !== undefined &&
            formId !== undefined
          ) {
            const answers = gnov(data.answers, `${fid}.0.answers`);

            gizmoData['sd'] = {
              f_id,
              filledFormId,
              formId,
              answers,
            };
          } else if (f_id !== undefined) {
            const formId = gnov(data.answers, `${fid}.0.formId`);
            const id = gnov(data.answers, `${fid}.0.id`);
            const version = gnov(data.answers, `${fid}.0.version`);
            const _id = gnov(data.answers, `${fid}.0._id`);
            const catalogFid = gnov(data.answers, `${fid}.0.catalogFid`);
            const parentOptionId = gnov(
              data.answers,
              `${fid}.0.parentOptionId`
            );

            gizmoData['sod'] = {
              f_id,
              value,
              formId,
              catalogFid,
            };

            if (id) {
              gizmoData.sod.id = id;
            }
            if (_id) {
              gizmoData.sod._id = _id;
            }
            if (parentOptionId) {
              gizmoData.sod.parentOptionId = parentOptionId;
            }
            if (version) {
              gizmoData.sod.version = version;
            }
          } else if (ocrResult !== undefined && attachmentId !== undefined) {
            const onlyFront = gnov(data.answers, `${fid}.0.onlyFront`);
            const extension = gnov(data.answers, `${fid}.0.extension`);
            const size = gnov(data.answers, `${fid}.0.size`);
            const name = gnov(data.answers, `${fid}.0.name`);
            const type = gnov(data.answers, `${fid}.0.type`);

            gizmoData['id'] = {
              attachmentId,
              ocrResult,
              source: 'web',
              onlyFront,

              fileExtension: extension,
              fileSize: size,
              fileName: name,
              fileType: type,
            };
          } else if (attachmentId !== undefined) {
            const extension = gnov(data.answers, `${fid}.0.extension`);
            const size = gnov(data.answers, `${fid}.0.size`);
            const name = gnov(data.answers, `${fid}.0.name`);
            const type = gnov(data.answers, `${fid}.0.type`);

            gizmoData['fd'] = {
              attachmentId,
              fileExtension: extension,
              fileSize: size,
              fileName: name,
              fileType: type,
            };
          } else if (timestamp !== undefined) {
            const timezone = gnov(data.answers, `${fid}.0.timezone`);

            gizmoData['dd'] = {
              timestamp,
            };

            if (timezone) {
              gizmoData['dd'].timezone = timezone;
            }
          }

          rootGizmoData.d[fid] = gizmoData;
        } else if (fid.startsWith('f')) {
          const embeddedFormData = gnov(data.answers, `${fid}`);

          if (Object.keys(embeddedFormData).length > 0) {
            const annexFormData: IGizmoData[] = [];

            for (const answerIndex in embeddedFormData) {
              const d = embeddedFormData[answerIndex];
              const gizmoData: IGizmoData = {
                v: '',
                afd: {
                  filledFormId: d.filledFormId,
                  formId: d.formId,
                  id: d.id,
                  columns: d.columns,
                  editable: d.editable,
                },
              };

              annexFormData.push(gizmoData);
            }

            rootGizmoData.d[fid] = annexFormData;
          }
        }
      } else if (Object.keys(data.answers[fid]).length > 1) {
        const gizmoData: IGizmoData[] = [];
        const gDataV2 = gnov(data.answers, `${fid}`);

        let idOcrData = false;
        for (const answerIndex in gDataV2) {
          const d = gDataV2[answerIndex];
          const f_id = gnov(d, `f_id`);
          const attachmentId = gnov(
            data.answers,
            `${fid}.${answerIndex}.attachmentId`
          );
          const ocrResult = gnov(
            data.answers,
            `${fid}.${answerIndex}.ocrResult`
          );

          if (ocrResult) {
            // We're doing this because only the first gizmo data has the ocrResult
            idOcrData = true;
          }

          if (f_id !== undefined) {
            // This means it's a multiple selected options gizmo data
            const value = gnov(d, `value`);
            const formId = gnov(d, `formId`);
            const id = gnov(d, `id`);
            const version = gnov(d, `version`);
            const _id = gnov(d, `_id`);
            const parentOptionId = gnov(
              data.answers,
              `${fid}.${answerIndex}.parentOptionId`
            );

            const gDataV3: IGizmoData = {
              v: value,
              sod: {
                f_id,
                value,
                formId,
                version,
              },
            };
            if (id) {
              gDataV3.sod!.id = id;
            }
            if (_id) {
              gDataV3.sod!._id = _id;
            }
            if (parentOptionId) {
              gDataV3.sod!.parentOptionId = parentOptionId;
            }

            gizmoData.push(gDataV3);
          } else if (fid.startsWith('f')) {
            const gDataV3: IGizmoData = {
              v: '',
              afd: {
                filledFormId: d.filledFormId,
                formId: d.formId,
                id: d.id,
                columns: d.columns,
                editable: d.editable,
              },
            };
            gizmoData.push(gDataV3);
          } else if (idOcrData && attachmentId !== undefined) {
            const gDataV3: IGizmoData = {
              v: attachmentId,
              id: {
                attachmentId,
                source: d.source,

                fileExtension: d.extension,
                fileSize: d.size,
                fileName: d.name,
                fileType: d.type,

                onlyFront: d.onlyFront,
              },
            };
            if (answerIndex === '0') {
              gDataV3.id!.ocrResult = ocrResult;
            }
            gizmoData.push(gDataV3);
          } else if (attachmentId !== undefined) {
            const gDataV3: IGizmoData = {
              v: attachmentId,
              fd: {
                attachmentId,
                fileExtension: d.extension,
                fileSize: d.size,
                fileName: d.name,
                fileType: d.type,
              },
            };
            gizmoData.push(gDataV3);
          }
        }

        rootGizmoData.d[fid] = gizmoData;
      }
    }
  }

  return rootGizmoData;
}

export default rootGizmoDataV2ToV3;
